import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@material-ui/core";
import { Send as SendIcon } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";
import type { QuestionnaireInMessage } from "../../../utils/signatureQuestionnaireStep";
import { questionnaireLabelContext } from "../../../utils/signatureQuestionnaireName";

let titleSequence = 0;

export type WhatsappConfirmDialogProps = {
  open: boolean;
  onCancel: () => void;
  /** Called once the realtor confirms. The handler may return a Promise; the dialog keeps a loading
   * state until it resolves. */
  onConfirm: () => void | Promise<void>;
  /** The client's name, on its own line under the explanation. */
  signerName?: string;
  /** The contract belongs to a set: the message carries one link for every contract still waiting. */
  inSet?: boolean;
  /** The number the message goes to, as the realtor reads it (`deliveryRecipientLabel`). */
  signerPhone?: string;
  /**
   * The owner questionnaire the message leads the client to — this contract's, or another contract's
   * of its set — from `questionnaireInMessage` (utils/signatureQuestionnaireStep.ts). Absent: none.
   */
  questionnaire?: QuestionnaireInMessage | null;
  /** Stacking override for callers rendering this from inside the signatures
   *  detail side-panel — a hand-rolled fixed overlay at zIndex 2001 that a
   *  default 1300 modal opens underneath. */
  zIndex?: number;
};

/**
 * Asked before EVERY automatic send of a signing link (the paper plane: the sent list, the detail
 * panel, the client card's signatures tab) — Trello #231 item 7, Haim 2026-10-21: "make sure we have a
 * confirmation before sending when clicking the paper plane icon". It says to which number the link
 * goes, that it goes from Webot's number by WhatsApp AND SMS (so it will not appear in the realtor's
 * own WhatsApp), and, for a contract of a set, that the message carries the set's one link. When
 * the message leads to the owner questionnaire (the contract carries it, or the contract of its set
 * that does), one line says so and when the client meets it (Trello #231, item 8 follow-up).
 *
 * There is no "don't show this again": until 2026-10-21 a ticked box made the list send at a single
 * tap from then on, and the panel and the client card never asked at all. The preference it stored
 * (`webot.signatures.waConfirmSkip` in localStorage) is no longer read anywhere.
 */
export default function WhatsappConfirmDialog({
  open,
  onCancel,
  onConfirm,
  signerName,
  signerPhone,
  inSet,
  questionnaire,
  zIndex
}: WhatsappConfirmDialogProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const [sending, setSending] = useState(false);
  // One send per confirmation: a second tap arriving before the button disables is dropped.
  const inFlight = useRef(false);
  const [titleId] = useState(() => `wa-confirm-title-${(titleSequence += 1)}`);

  const handleConfirm = async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    try {
      await onConfirm();
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  const handleCancel = (): void => {
    if (inFlight.current) return;
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby={titleId}
      sx={zIndex ? { zIndex } : undefined}
    >
      <DialogTitle id={titleId}>{t("signatures.waConfirm.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2">
            {signerPhone ? t("signatures.waConfirm.bodyTo", { to: signerPhone }) : t("signatures.waConfirm.body")}
          </Typography>
          {inSet && <Typography variant="body2">{t("signatures.waConfirm.setNote")}</Typography>}
          {questionnaire && (
            <Typography variant="body2" data-testid="wa-confirm-questionnaire">
              {t(
                `signatures.waConfirm.${questionnaire.step === "awaiting" ? "questionnaireAwaiting" : "questionnaireNote"}${
                  questionnaire.carrierTitle ? "Set" : ""
                }`,
                { title: questionnaire.carrierTitle, context: questionnaireLabelContext(questionnaire.listedFor) }
              )}
            </Typography>
          )}
          {signerName && (
            <Typography variant="body2" color="text.secondary">
              {signerName}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={sending}>
          {t("signatures.cancel")}
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={
            // A paper plane, not the WhatsApp logo: this is Webot's automatic send (its own
            // number, plus SMS). The logo is kept for the realtor's OWN WhatsApp (share).
            sending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SendIcon sx={theme.direction === "rtl" ? { transform: "scaleX(-1)" } : undefined} />
            )
          }
          onClick={handleConfirm}
          disabled={sending}
        >
          {t("signatures.waConfirm.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
